import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type {
  ExchangeTransferCode,
  LoadSession,
  SetSession,
} from '@/modules/auth/use-cases'
import type { LoadMemberContext } from '@/shared/domain'
import { isTrackingOnlyRole } from '@/shared/domain'
import { validateRedirectUrl } from '@/modules/auth/ui/validate-redirect'

export type UseTransferPageProps = {
  onExchangeTransferCode: ExchangeTransferCode
  onSetSession: SetSession
  onLoadSession: LoadSession
  onLoadMemberContext: LoadMemberContext
}

export type TransferPageStatus = 'loading' | 'access-denied' | 'failed'

export type UseTransferPageResult = {
  status: TransferPageStatus
  message: string
  onGoToLogin: () => void
}

export const LOADING_MESSAGES = [
  'Verifying code…',
  'Exchanging your code…',
  'Setting up your session…',
  'Verifying permissions…',
  'Redirecting…',
] as const

export const MESSAGE_CYCLE_INTERVAL_MS = 600

function appendOrgId(target: string, orgId: string | null): string {
  if (!orgId) return target
  const separator = target.includes('?') ? '&' : '?'
  return `${target}${separator}org=${encodeURIComponent(orgId)}`
}

function storeMobileRedirect(redirect: string | null, deepLinkScheme: string | null): void {
  if (redirect !== 'mobile' || !deepLinkScheme) return
  window.sessionStorage.setItem('mobileDeepLinkScheme', deepLinkScheme)
  window.sessionStorage.setItem('redirectToMobile', 'true')
}

export function useTransferPage({
  onExchangeTransferCode,
  onSetSession,
  onLoadSession,
  onLoadMemberContext,
}: UseTransferPageProps): UseTransferPageResult {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hasHandledRef = useRef<boolean>(false)
  const [status, setStatus] = useState<TransferPageStatus>('loading')
  const [messageIndex, setMessageIndex] = useState<number>(0)

  useEffect(() => {
    if (hasHandledRef.current) return
    hasHandledRef.current = true

    const code = searchParams.get('code')
    const orgId = searchParams.get('org_id')
    const redirectPath = searchParams.get('redirect_path')
    const redirect = searchParams.get('redirect')
    const deepLinkScheme = searchParams.get('deepLinkScheme')

    storeMobileRedirect(redirect, deepLinkScheme)

    if (!code) {
      setStatus('failed')
      return
    }

    const run = async () => {
      try {
        const { accessToken, refreshToken } = await onExchangeTransferCode({ code })
        await onSetSession({ accessToken, refreshToken })
        const user = await onLoadSession()
        if (!user) {
          setStatus('failed')
          return
        }
        const context = await onLoadMemberContext({})
        if (!context?.user?.id) {
          setStatus('access-denied')
          return
        }
        const canAccess = (context.orgMemberships ?? []).some(
          (entry) =>
            entry.membership.status === 'active' &&
            !isTrackingOnlyRole(entry.membership.role, entry.org.orgType),
        )
        if (!canAccess) {
          setStatus('access-denied')
          return
        }
        const safeBase = validateRedirectUrl(redirectPath) ?? '/dashboard'
        const target = appendOrgId(safeBase, orgId)
        navigate(target, { replace: true })
      } catch {
        setStatus('failed')
      }
    }

    void run()
  }, [
    navigate,
    onExchangeTransferCode,
    onLoadMemberContext,
    onLoadSession,
    onSetSession,
    searchParams,
  ])

  useEffect(() => {
    if (status !== 'loading') return
    const id = window.setInterval(() => {
      setMessageIndex((prev) => Math.min(prev + 1, LOADING_MESSAGES.length - 1))
    }, MESSAGE_CYCLE_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [status])

  const onGoToLogin = (): void => {
    navigate('/sign-in', { replace: true })
  }

  return {
    status,
    message: LOADING_MESSAGES[messageIndex] ?? LOADING_MESSAGES[0],
    onGoToLogin,
  }
}
